import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  loadDexData,
  buildEvolutionChain,
  parseLearnset,
  DexSpecies,
  DexDataBundle,
  EvolutionStep,
  ParsedLearnsetMove,
} from '../../../shared/utils/pokedexDataLoader';
import { getPokemonSlug, getPokemonDexUrl } from '../../../shared/utils/pokemonDexUrl';
import { getIconName } from '../../../utils/speciesUtils';
import { getEggMoves, EggMoveInfo } from '../../../utils/eggMovesData';
import './PokemonSidebarTab.scss';

const TYPE_COLORS: Record<string, string> = {
  Normal: '#A8A878',
  Fire: '#F08030',
  Water: '#6890F0',
  Electric: '#F8D030',
  Grass: '#78C850',
  Ice: '#98D8D8',
  Fighting: '#C03028',
  Poison: '#A040A0',
  Ground: '#E0C068',
  Flying: '#A890F0',
  Psychic: '#F85888',
  Bug: '#A8B820',
  Rock: '#B8A038',
  Ghost: '#705898',
  Dragon: '#7038F8',
  Dark: '#705848',
  Steel: '#B8B8D0',
  Fairy: '#EE99AC',
};

function getStatColor(val: number): string {
  if (val >= 130) return '#22c55e'; // Green (Elite)
  if (val >= 100) return '#84cc16'; // Lime (Great)
  if (val >= 80) return '#eab308';  // Yellow (Good)
  if (val >= 60) return '#f97316';  // Orange (Average)
  return '#ef4444';                 // Red (Low)
}

function getCategoryIcon(cat: string): string {
  if (cat === 'Physical') return '⚔️';
  if (cat === 'Special') return '✨';
  return '🛡️';
}

export function resolveSpeciesSlug(name: string, speciesMap: Record<string, DexSpecies>): string | null {
  if (!name || name === 'Egg') return null;
  const slug = getPokemonSlug(name);
  if (speciesMap[slug]) return slug;

  const lowerSlug = slug.toLowerCase();
  for (const s of Object.keys(speciesMap)) {
    if (s.toLowerCase() === lowerSlug) return s;
  }

  const cleanName = name.toLowerCase().replace(/[^a-z0-9]/g, '');
  for (const [s, sp] of Object.entries(speciesMap)) {
    if (sp.name.toLowerCase() === name.toLowerCase()) return s;
    if (sp.name.toLowerCase().replace(/[^a-z0-9]/g, '') === cleanName) return s;
  }

  for (const [s] of Object.entries(speciesMap)) {
    if (cleanName.startsWith(s) || s.startsWith(cleanName)) return s;
  }

  return null;
}

interface PokemonSidebarTabProps {
  myParty?: any[];
  myBox?: any[];
  initialPokemonName?: string;
  inspectKey?: number;
}

export const PokemonSidebarTab: React.FC<PokemonSidebarTabProps> = ({
  myParty,
  initialPokemonName,
  inspectKey,
}) => {
  const [dataBundle, setDataBundle] = useState<DexDataBundle | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedSlug, setSelectedSlug] = useState<string>('bulbasaur');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isSearchFocused, setIsSearchFocused] = useState<boolean>(false);
  const [activeMoveTab, setActiveMoveTab] = useState<'level' | 'tm' | 'tutor' | 'egg'>('level');
  const [moveSearchQuery, setMoveSearchQuery] = useState<string>('');
  const [recentSlugs, setRecentSlugs] = useState<string[]>([]);
  const [expandedMoveSlug, setExpandedMoveSlug] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const hasInitializedDefaultRef = useRef<boolean>(false);

  // Keep recently viewed list up to date
  const handleSelectSpecies = (slug: string) => {
    setSelectedSlug(slug);
    setSearchQuery('');
    setIsSearchFocused(false);
    setMoveSearchQuery('');
    setExpandedMoveSlug(null);

    setRecentSlugs((prev) => {
      const filtered = prev.filter((s) => s !== slug);
      return [slug, ...filtered].slice(0, 5);
    });

    containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Load Pokédex data bundle on mount
  useEffect(() => {
    let mounted = true;
    loadDexData()
      .then((bundle) => {
        if (!mounted) return;
        setDataBundle(bundle);
        setLoading(false);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err.message || 'Failed to load Pokédex data');
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  // Inspect Pokémon or default to 1st party member once loaded
  useEffect(() => {
    if (!dataBundle) return;

    if (initialPokemonName) {
      const slug = resolveSpeciesSlug(initialPokemonName, dataBundle.species);
      if (slug) {
        handleSelectSpecies(slug);
        return;
      }
    }

    // Default to the 1st party member on initial mount if no inspect name
    if (!hasInitializedDefaultRef.current && myParty && myParty.length > 0) {
      const first = myParty.find((p) => p?.name && p.name !== 'Egg');
      if (first?.name) {
        hasInitializedDefaultRef.current = true;
        const partySlug = resolveSpeciesSlug(first.name, dataBundle.species);
        if (partySlug) {
          handleSelectSpecies(partySlug);
        }
      }
    }
  }, [initialPokemonName, inspectKey, dataBundle]);

  // Active species details
  const activeSpecies: DexSpecies | null = useMemo(() => {
    if (!dataBundle) return null;
    return dataBundle.species[selectedSlug] || null;
  }, [dataBundle, selectedSlug]);

  // Evolution chain
  const evoChain: EvolutionStep[] = useMemo(() => {
    if (!dataBundle || !selectedSlug) return [];
    return buildEvolutionChain(selectedSlug, dataBundle.species);
  }, [dataBundle, selectedSlug]);

  // Learnsets
  const parsedMoves = useMemo(() => {
    if (!dataBundle || !selectedSlug) {
      return { levelUp: [], tms: [], tutors: [] };
    }
    return parseLearnset(selectedSlug, dataBundle.learnsets, dataBundle.moves);
  }, [dataBundle, selectedSlug]);

  // Emerald Blitz Egg Moves
  const eggMoves: EggMoveInfo[] = useMemo(() => {
    if (!activeSpecies) return [];
    return getEggMoves(activeSpecies.name, activeSpecies.num);
  }, [activeSpecies]);

  // Filtered search suggestions
  const searchResults = useMemo(() => {
    if (!dataBundle || !searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase().trim();
    const all = Object.entries(dataBundle.species);

    const matches: Array<{ slug: string; species: DexSpecies; score: number }> = [];

    for (const [slug, sp] of all) {
      const nameLower = sp.name.toLowerCase();
      const numStr = String(sp.num);

      let score = -1;
      if (nameLower === q) {
        score = 100;
      } else if (nameLower.startsWith(q)) {
        score = 80;
      } else if (nameLower.includes(q)) {
        score = 50;
      } else if (numStr === q) {
        score = 90;
      } else if (numStr.startsWith(q)) {
        score = 40;
      } else if (sp.types.some((t) => t.toLowerCase().startsWith(q))) {
        score = 30;
      }

      if (score > 0) {
        // Boost obtainable Pokémon
        if (sp.tier === 'obtainable') score += 10;
        matches.push({ slug, species: sp, score });
      }
    }

    matches.sort((a, b) => b.score - a.score || a.species.num - b.species.num);
    return matches.slice(0, 15);
  }, [dataBundle, searchQuery]);

  // Filtered learnset moves based on sub-search
  const filteredLevelUp = useMemo(() => {
    if (!moveSearchQuery.trim()) return parsedMoves.levelUp;
    const q = moveSearchQuery.toLowerCase().trim();
    return parsedMoves.levelUp.filter(
      (m) => m.name.toLowerCase().includes(q) || m.type.toLowerCase().includes(q)
    );
  }, [parsedMoves.levelUp, moveSearchQuery]);

  const filteredTms = useMemo(() => {
    if (!moveSearchQuery.trim()) return parsedMoves.tms;
    const q = moveSearchQuery.toLowerCase().trim();
    return parsedMoves.tms.filter(
      (m) => m.name.toLowerCase().includes(q) || m.type.toLowerCase().includes(q)
    );
  }, [parsedMoves.tms, moveSearchQuery]);

  const filteredTutors = useMemo(() => {
    if (!moveSearchQuery.trim()) return parsedMoves.tutors;
    const q = moveSearchQuery.toLowerCase().trim();
    return parsedMoves.tutors.filter(
      (m) => m.name.toLowerCase().includes(q) || m.type.toLowerCase().includes(q)
    );
  }, [parsedMoves.tutors, moveSearchQuery]);

  if (loading) {
    return (
      <div className="pokemon-sidebar-loading">
        <div className="dex-spinner" />
        <span>Loading Pokédex data...</span>
      </div>
    );
  }

  if (error || !dataBundle || !activeSpecies) {
    return (
      <div className="pokemon-sidebar-error">
        <span>Failed to load Pokémon data.</span>
        <button onClick={() => window.location.reload()} className="dex-retry-btn">
          Retry
        </button>
      </div>
    );
  }

  const { hp, atk, def, spa, spd, spe } = activeSpecies.baseStats;
  const bst = hp + atk + def + spa + spd + spe;
  const iconBase = getIconName(activeSpecies.name, activeSpecies.num, activeSpecies.forme);
  const spriteUrl = `https://play.pokemonshowdown.com/sprites/gen5/${selectedSlug}.png`;
  const dexUrl = getPokemonDexUrl(activeSpecies.name, activeSpecies.forme);

  return (
    <div className="pokemon-sidebar-tab" ref={containerRef}>
      {/* ── Quick Team Row ─────────────────────────────────────────── */}
      {myParty && myParty.length > 0 && (
        <div className="dex-team-quickrow">
          <div className="team-row-label">Your Team:</div>
          <div className="team-chips-list">
            {myParty.map((p, idx) => {
              const partySlug = (dataBundle && resolveSpeciesSlug(p.name, dataBundle.species)) || getPokemonSlug(p.name);
              const partyIcon = getIconName(p.name, p.pokedex_id || p.species_id);
              const isSelected = partySlug === selectedSlug;

              return (
                <button
                  key={`party-${idx}`}
                  type="button"
                  className={`team-mon-chip ${isSelected ? 'active' : ''}`}
                  onClick={() => handleSelectSpecies(partySlug)}
                  title={`${p.name}${p.nickname && p.nickname !== p.name ? ` (${p.nickname})` : ''}`}
                >
                  <img
                    src={`/MiniIcons/${partyIcon}.png`}
                    alt={p.name}
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).src = '/MiniIcons/egg.png';
                    }}
                  />
                  <span className="team-mon-name">{p.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Search Bar ────────────────────────────────────────────── */}
      <div className="dex-search-wrapper">
        <div className="dex-search-input-box">
          <span className="search-icon">🔍</span>
          <input
            ref={searchInputRef}
            type="text"
            className="dex-search-input"
            placeholder="Search name, dex #, type..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setIsSearchFocused(true)}
          />
          {searchQuery && (
            <button
              type="button"
              className="search-clear-btn"
              onClick={() => {
                setSearchQuery('');
                searchInputRef.current?.focus();
              }}
            >
              ✕
            </button>
          )}
        </div>

        {/* Autocomplete Dropdown */}
        {isSearchFocused && searchResults.length > 0 && (
          <div className="dex-search-dropdown">
            {searchResults.map(({ slug, species }) => {
              const itemIcon = getIconName(species.name, species.num, species.forme);
              return (
                <div
                  key={slug}
                  className={`search-dropdown-item ${slug === selectedSlug ? 'selected' : ''}`}
                  onMouseDown={() => handleSelectSpecies(slug)}
                >
                  <img
                    src={`/MiniIcons/${itemIcon}.png`}
                    alt=""
                    className="item-icon"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).src = '/MiniIcons/egg.png';
                    }}
                  />
                  <div className="item-info">
                    <span className="item-num">#{String(species.num).padStart(3, '0')}</span>
                    <span className="item-name">{species.name}</span>
                  </div>
                  <div className="item-types">
                    {species.types.map((t) => (
                      <span
                        key={t}
                        className="item-type-badge"
                        style={{ backgroundColor: TYPE_COLORS[t] || '#666' }}
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  {species.tier === 'obtainable' && (
                    <span className="item-obtainable-dot" title="Obtainable in Emerald Blitz">
                      ●
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Recent Pokémon Chips ───────────────────────────────────── */}
      {recentSlugs.length > 1 && (
        <div className="dex-recent-row">
          <span className="recent-label">Recent:</span>
          {recentSlugs.map((slug) => {
            const sp = dataBundle.species[slug];
            if (!sp) return null;
            return (
              <button
                key={slug}
                type="button"
                className={`recent-chip ${slug === selectedSlug ? 'active' : ''}`}
                onClick={() => handleSelectSpecies(slug)}
              >
                {sp.name}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Hero Pokémon Info Card ─────────────────────────────────── */}
      <div className="dex-pokemon-hero-card">
        <div className="hero-sprite-container">
          <img
            src={spriteUrl}
            alt={activeSpecies.name}
            className="hero-sprite"
            onError={(e) => {
              // Fallback to local MiniIcon
              (e.currentTarget as HTMLImageElement).src = `/MiniIcons/${iconBase}.png`;
            }}
          />
        </div>

        <div className="hero-details">
          <div className="hero-header-row">
            <span className="hero-dex-num">#{String(activeSpecies.num).padStart(3, '0')}</span>
            <span
              className={`hero-tier-badge ${activeSpecies.tier === 'obtainable' ? 'obtainable' : 'not-obtainable'}`}
            >
              {activeSpecies.tier === 'obtainable' ? 'Obtainable in Blitz' : 'Not in Blitz'}
            </span>
          </div>

          <h3 className="hero-pokemon-name">{activeSpecies.name}</h3>

          <div className="hero-types-row">
            {activeSpecies.types.map((t) => (
              <span
                key={t}
                className="hero-type-pill"
                style={{ backgroundColor: TYPE_COLORS[t] || '#666' }}
              >
                {t}
              </span>
            ))}
          </div>

          <a
            href={dexUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hero-external-dex-link"
            title="Open comprehensive Dex page in new tab"
          >
            📖 Open in Dex ↗
          </a>
        </div>
      </div>

      {/* ── Base Stats Section ─────────────────────────────────────── */}
      <div className="dex-card dex-stats-card">
        <div className="dex-card-title">
          <span>Base Stats</span>
          <span className="bst-badge">BST: {bst}</span>
        </div>

        <div className="stats-bars-container">
          {[
            { label: 'HP', val: hp },
            { label: 'ATK', val: atk },
            { label: 'DEF', val: def },
            { label: 'SPA', val: spa },
            { label: 'SPD', val: spd },
            { label: 'SPE', val: spe },
          ].map(({ label, val }) => {
            const barWidth = Math.min(Math.round((val / 200) * 100), 100);
            const color = getStatColor(val);

            return (
              <div key={label} className="stat-bar-row">
                <span className="stat-label">{label}</span>
                <span className="stat-val">{val}</span>
                <div className="stat-bar-track">
                  <div
                    className="stat-bar-fill"
                    style={{
                      width: `${barWidth}%`,
                      backgroundColor: color,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Abilities Section ──────────────────────────────────────── */}
      <div className="dex-card dex-abilities-card">
        <div className="dex-card-title">Abilities</div>
        <div className="abilities-list">
          {Object.entries(activeSpecies.abilities).map(([slotKey, abName]) => {
            const abSlug = getPokemonSlug(abName);
            const abInfo = dataBundle.abilities[abSlug];
            const isHidden = slotKey === 'H';

            return (
              <div key={slotKey} className={`ability-entry ${isHidden ? 'hidden-ability' : ''}`}>
                <div className="ability-header">
                  <span className="ability-name">{abName}</span>
                  {isHidden && <span className="ha-tag">Hidden</span>}
                </div>
                <div className="ability-desc">
                  {abInfo?.shortDesc || abInfo?.desc || 'No description available.'}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Evolution Line Section ─────────────────────────────────── */}
      {evoChain.length > 0 && (
        <div className="dex-card dex-evolution-card">
          <div className="dex-card-title">Evolution Line</div>
          <div className="evolution-chain-flow">
            {evoChain.map((step, idx) => {
              const stepIcon = getIconName(step.name, step.num);
              return (
                <React.Fragment key={step.slug}>
                  {idx > 0 && (
                    <div className="evo-arrow-container">
                      <span className="evo-arrow">➔</span>
                      {step.trigger && <span className="evo-trigger-label">{step.trigger}</span>}
                    </div>
                  )}
                  <button
                    type="button"
                    className={`evo-step-chip ${step.isCurrent ? 'current' : ''}`}
                    onClick={() => handleSelectSpecies(step.slug)}
                    title={`View ${step.name}`}
                  >
                    <img
                      src={`/MiniIcons/${stepIcon}.png`}
                      alt={step.name}
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).src = '/MiniIcons/egg.png';
                      }}
                    />
                    <div className="evo-step-details">
                      <span className="evo-step-name">{step.name}</span>
                      <span className="evo-step-types">
                        {step.types.join('/')}
                      </span>
                    </div>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Emerald Blitz Egg Moves (Mauville Day Care) ─────────────── */}
      {eggMoves.length > 0 && (
        <div className="dex-card dex-egg-moves-card">
          <div className="dex-card-title">
            <span>Mauville Day Care Egg Move</span>
            <span className="daycare-pill">Emerald Blitz</span>
          </div>
          <div className="egg-move-spotlight">
            <div className="egg-move-header">
              <span className="egg-icon">🥚</span>
              <span className="egg-move-name">{eggMoves[0].name}</span>
              <span
                className="egg-type-badge"
                style={{ backgroundColor: TYPE_COLORS[eggMoves[0].type] || '#777' }}
              >
                {eggMoves[0].type}
              </span>
              <span className="egg-cat-badge">{getCategoryIcon(eggMoves[0].category)} {eggMoves[0].category}</span>
            </div>
            <div className="egg-stats-row">
              <span>Power: {eggMoves[0].power > 0 ? eggMoves[0].power : '—'}</span>
              <span>•</span>
              <span>Acc: {typeof eggMoves[0].accuracy === 'number' ? `${eggMoves[0].accuracy}%` : '—'}</span>
              <span>•</span>
              <span>PP: {eggMoves[0].pp}</span>
            </div>
            <div className="egg-move-desc">{eggMoves[0].desc}</div>
            <div className="daycare-instruction">
              💡 Leave at Mauville Day Care for 1 gym badge to replace Move Slot 1 with this Egg Move.
            </div>

            {eggMoves.length > 1 && (
              <div className="family-egg-moves-section">
                <div className="family-title">Other Family Egg Moves:</div>
                <div className="family-grid">
                  {eggMoves.slice(1).map((em) => (
                    <div key={em.id} className="family-chip">
                      <span
                        className="family-type-dot"
                        style={{ backgroundColor: TYPE_COLORS[em.type] || '#777' }}
                      />
                      <span className="family-name">{em.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Learnsets Section ───────────────────────────────────────── */}
      <div className="dex-card dex-learnsets-card">
        <div className="dex-card-title">Learnset</div>

        {/* Sub-tabs for Level Up / TMs / Tutors */}
        <div className="learnset-subtabs">
          <button
            type="button"
            className={`learnset-tab-btn ${activeMoveTab === 'level' ? 'active' : ''}`}
            onClick={() => setActiveMoveTab('level')}
          >
            Level Up ({parsedMoves.levelUp.length})
          </button>
          <button
            type="button"
            className={`learnset-tab-btn ${activeMoveTab === 'tm' ? 'active' : ''}`}
            onClick={() => setActiveMoveTab('tm')}
          >
            TM / HM ({parsedMoves.tms.length})
          </button>
          <button
            type="button"
            className={`learnset-tab-btn ${activeMoveTab === 'tutor' ? 'active' : ''}`}
            onClick={() => setActiveMoveTab('tutor')}
          >
            Tutor ({parsedMoves.tutors.length})
          </button>
        </div>

        {/* Quick move filter */}
        <div className="move-search-filter">
          <input
            type="text"
            className="move-search-input"
            placeholder="Filter moves (e.g. Earthquake)..."
            value={moveSearchQuery}
            onChange={(e) => setMoveSearchQuery(e.target.value)}
          />
          {moveSearchQuery && (
            <button
              type="button"
              className="move-search-clear"
              onClick={() => setMoveSearchQuery('')}
            >
              ✕
            </button>
          )}
        </div>

        {/* Moves Table */}
        <div className="moves-table-container">
          {activeMoveTab === 'level' && (
            filteredLevelUp.length > 0 ? (
              <table className="dex-moves-table">
                <thead>
                  <tr>
                    <th style={{ width: '45px' }}>Lv</th>
                    <th>Move</th>
                    <th style={{ width: '65px' }}>Type</th>
                    <th style={{ width: '40px' }}>Pwr</th>
                    <th style={{ width: '40px' }}>Acc</th>
                    <th style={{ width: '35px' }}>PP</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLevelUp.map((m, idx) => {
                    const isExpanded = expandedMoveSlug === `${m.slug}-${idx}`;
                    return (
                      <React.Fragment key={`${m.slug}-${idx}`}>
                        <tr
                          className={`move-row ${isExpanded ? 'expanded' : ''}`}
                          onClick={() =>
                            setExpandedMoveSlug(isExpanded ? null : `${m.slug}-${idx}`)
                          }
                          title="Click to view move effect"
                        >
                          <td className="move-lvl">{m.methodDisplay}</td>
                          <td className="move-name">
                            <span className="cat-icon">{getCategoryIcon(m.category)}</span>
                            {m.name}
                          </td>
                          <td className="move-type">
                            <span
                              className="type-tag"
                              style={{ backgroundColor: TYPE_COLORS[m.type] || '#666' }}
                            >
                              {m.type}
                            </span>
                          </td>
                          <td className="move-stat">{m.basePower > 0 ? m.basePower : '—'}</td>
                          <td className="move-stat">
                            {typeof m.accuracy === 'number' ? m.accuracy : '—'}
                          </td>
                          <td className="move-stat">{m.pp}</td>
                        </tr>
                        {isExpanded && (
                          <tr className="move-desc-row">
                            <td colSpan={6}>
                              <div className="move-desc-box">
                                <span className="cat-badge">{m.category}</span>
                                <span className="desc-text">
                                  {m.desc || 'No additional effect.'}
                                </span>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="moves-empty">No level-up moves found.</div>
            )
          )}

          {activeMoveTab === 'tm' && (
            filteredTms.length > 0 ? (
              <table className="dex-moves-table">
                <thead>
                  <tr>
                    <th style={{ width: '45px' }}>Type</th>
                    <th>Move</th>
                    <th style={{ width: '65px' }}>Element</th>
                    <th style={{ width: '40px' }}>Pwr</th>
                    <th style={{ width: '40px' }}>Acc</th>
                    <th style={{ width: '35px' }}>PP</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTms.map((m, idx) => {
                    const isExpanded = expandedMoveSlug === `tm-${m.slug}-${idx}`;
                    return (
                      <React.Fragment key={`tm-${m.slug}-${idx}`}>
                        <tr
                          className={`move-row ${isExpanded ? 'expanded' : ''}`}
                          onClick={() =>
                            setExpandedMoveSlug(isExpanded ? null : `tm-${m.slug}-${idx}`)
                          }
                          title="Click to view move effect"
                        >
                          <td className="move-lvl">TM</td>
                          <td className="move-name">
                            <span className="cat-icon">{getCategoryIcon(m.category)}</span>
                            {m.name}
                          </td>
                          <td className="move-type">
                            <span
                              className="type-tag"
                              style={{ backgroundColor: TYPE_COLORS[m.type] || '#666' }}
                            >
                              {m.type}
                            </span>
                          </td>
                          <td className="move-stat">{m.basePower > 0 ? m.basePower : '—'}</td>
                          <td className="move-stat">
                            {typeof m.accuracy === 'number' ? m.accuracy : '—'}
                          </td>
                          <td className="move-stat">{m.pp}</td>
                        </tr>
                        {isExpanded && (
                          <tr className="move-desc-row">
                            <td colSpan={6}>
                              <div className="move-desc-box">
                                <span className="cat-badge">{m.category}</span>
                                <span className="desc-text">
                                  {m.desc || 'No additional effect.'}
                                </span>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="moves-empty">No TM / HM moves found.</div>
            )
          )}

          {activeMoveTab === 'tutor' && (
            filteredTutors.length > 0 ? (
              <table className="dex-moves-table">
                <thead>
                  <tr>
                    <th style={{ width: '45px' }}>Type</th>
                    <th>Move</th>
                    <th style={{ width: '65px' }}>Element</th>
                    <th style={{ width: '40px' }}>Pwr</th>
                    <th style={{ width: '40px' }}>Acc</th>
                    <th style={{ width: '35px' }}>PP</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTutors.map((m, idx) => {
                    const isExpanded = expandedMoveSlug === `tutor-${m.slug}-${idx}`;
                    return (
                      <React.Fragment key={`tutor-${m.slug}-${idx}`}>
                        <tr
                          className={`move-row ${isExpanded ? 'expanded' : ''}`}
                          onClick={() =>
                            setExpandedMoveSlug(isExpanded ? null : `tutor-${m.slug}-${idx}`)
                          }
                          title="Click to view move effect"
                        >
                          <td className="move-lvl">Tutor</td>
                          <td className="move-name">
                            <span className="cat-icon">{getCategoryIcon(m.category)}</span>
                            {m.name}
                          </td>
                          <td className="move-type">
                            <span
                              className="type-tag"
                              style={{ backgroundColor: TYPE_COLORS[m.type] || '#666' }}
                            >
                              {m.type}
                            </span>
                          </td>
                          <td className="move-stat">{m.basePower > 0 ? m.basePower : '—'}</td>
                          <td className="move-stat">
                            {typeof m.accuracy === 'number' ? m.accuracy : '—'}
                          </td>
                          <td className="move-stat">{m.pp}</td>
                        </tr>
                        {isExpanded && (
                          <tr className="move-desc-row">
                            <td colSpan={6}>
                              <div className="move-desc-box">
                                <span className="cat-badge">{m.category}</span>
                                <span className="desc-text">
                                  {m.desc || 'No additional effect.'}
                                </span>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="moves-empty">No tutor moves found.</div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default PokemonSidebarTab;
